import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { sortTypesByDescription } from "src/pages/CrewSchedule/CrewScheduleHeader/AddEvents/ManageNFAs";
import { IBidPackageNonFlyingAssignments, INonFlyingType } from "@vmosolutions/oc-interfaces";
import { Grid } from "@mui/material";
import { FormikTextField } from "src/components/FormikComponents/FormikWrappers";

interface INFATypeCountFieldProps {
  mosaicPane: HTMLElement | null;
  value: IBidPackageNonFlyingAssignments[];
  onChange: (newValue: IBidPackageNonFlyingAssignments[]) => void;
}

/**
 * NFA types/counts field for Bid Package Editor
 * @param props -
 */
export const NFATypeCountField = (props: INFATypeCountFieldProps): JSX.Element => {
  const { value, onChange, mosaicPane } = props;

  // Non-Flying-Assignment Type Options
  const nonFlyingTypes = useSelector((state: IRootState) => state.structures.customerStructure.nonFlyingTypes);
  const nonFlyingTypesOptions = useMemo(
    () => nonFlyingTypes.filter((nonFlyingType: INonFlyingType) => nonFlyingType.enabled).sort(sortTypesByDescription),
    [nonFlyingTypes]
  );

  const selectedNFATypes = useMemo(() => {
    return value?.length ? value.map((nfa) => nfa.nfaType) : [];
  }, [value]);

  // Apply or remove selected types from existing type/counts
  const handleSelectNFAType = useCallback(
    (nonFlyingTypes: INonFlyingType[]) => {
      const newNFAs: IBidPackageNonFlyingAssignments[] = nonFlyingTypes.reduce((newCounts, type) => {
        const typeFound = value?.find((nfa) => nfa?.nfaType?._id === type._id);
        if (typeFound) {
          // If type already found, push it to new values.
          newCounts.push(typeFound);
        } else {
          // else create a new entry for this type with maxAmount set to 0 by default
          newCounts.push({ nfaType: type, maxAmount: 1 });
        }
        return newCounts;
      }, []);
      // Set new value for this field
      onChange(newNFAs);
    },
    [value, onChange]
  );

  return (
    <>
      <SelectField
        className="field selectfield nfa_type_selector"
        label="NFA Types (assignment count)"
        name="NFATypeSelector"
        options={nonFlyingTypesOptions}
        value={selectedNFATypes}
        onChange={handleSelectNFAType}
        getOptionLabel={(type: INonFlyingType) => `${type?.description} (${type?.title})`}
        getOptionValue={(type: INonFlyingType) => type._id}
        isMulti={true}
        isSearchable={true}
        menuPortalTarget={mosaicPane}
        menuPosition={"fixed"}
        openMenuOnFocus={false}
        isClearable={true}
        hideSelectedOptions={false}
        // placeholder="Select biddable Non-Flying Assignment Types for package..."
        // multiMode="checkbox"
      />

      <Grid container className="nfa_type_counts">
        {value?.map((nfa, index) => {
          // Only return nfa count component if nfatype exists.
          return nfa?.nfaType?._id ? (
            <Grid item key={`bid_package_nfa_${nfa?.nfaType?._id}_count`}>
              <FormikTextField
                className="field textfield nfa_type_count"
                label={`${nfa?.nfaType?.description} (${nfa?.nfaType?.title})`}
                name={`nonFlyingAssignments.${index}.maxAmount`}
                type="number"
              />
            </Grid>
          ) : null;
        })}
      </Grid>
    </>
  );
};
